import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';

/**
 * Krok AS1 — schránka PŘIHLÁŠENÉHO makléře (`/v1/me/mailbox*`). Stejný vzor
 * jako `assistant-settings-api.ts`: raw `fetch`, routy jsou nové a v
 * generovaném `@reos/api-client` schématu ještě nejsou.
 *
 * Heslo aplikace jde DOVNITŘ jen v těle „Ověřit" a „Připojit"; zpátky
 * nepřijde nikdy (server ho do odpovědi nedává) a tady se nikam neukládá.
 */

export type MailboxPresetKey = 'gmail' | 'seznam' | 'outlook' | 'custom';
export type MailKind = 'poptavka' | 'dokument' | 'platba' | 'ostatni' | 'bez_akce';

export interface MailboxDto {
  readonly id: string;
  readonly address: string;
  readonly preset: MailboxPresetKey | 'demo';
  readonly host: string;
  readonly port: number;
  readonly secure: boolean;
  readonly username: string;
  readonly shareWithOwner: boolean;
  readonly status: 'connected' | 'error' | 'demo';
  readonly lastCheckedAt: string | null;
  readonly lastError: string | null;
  /** Krok MAIL1 bod 2 — odchozí server téže schránky. `null` = schránka jen čte. */
  readonly smtpHost: string | null;
  readonly smtpPort: number | null;
  readonly smtpSecure: boolean | null;
  readonly smtpVerifiedAt: string | null;
  readonly smtpLastError: string | null;
}

export interface MailboxSummaryDto {
  readonly today: number;
  readonly needsYou: number;
  readonly matched: number;
  readonly noAction: number;
}

export interface MailboxViewDto {
  readonly connected: boolean;
  readonly mailbox: MailboxDto | null;
  readonly summary: MailboxSummaryDto | null;
  /** Krok MAIL1: `true` = klientské e-maily půjdou z adresy makléře. */
  readonly canSend?: boolean;
}

/** Krok MAIL1 bod 2: „Ověřit připojení" vrací ODDĚLENĚ čtení a odesílání. */
export interface MailboxProbeDto {
  readonly ok: boolean;
  readonly code: string | null;
}

export interface MailboxTestDto {
  readonly imap: MailboxProbeDto;
  readonly smtp: MailboxProbeDto;
}

export interface MailCardDto {
  readonly id: string;
  readonly kind: MailKind;
  readonly reason: string;
  readonly portal: string | null;
  readonly subject: string | null;
  readonly fromName: string | null;
  readonly fromEmail: string | null;
  readonly receivedAt: string;
  readonly excerpt: string;
  /**
   * Krok PROP5 bod 5: `isDocument` rozhoduje SERVER (tentýž filtr, který
   * určuje druh karty) — UI si pravidlo „co je dokument a co obrázek
   * z podpisu" neopisuje.
   */
  readonly attachments: readonly {
    readonly filename: string;
    readonly contentType: string;
    readonly size: number;
    readonly isDocument: boolean;
  }[];
  readonly caseId: string | null;
  readonly invoiceId: string | null;
  readonly chooseCase: boolean;
  readonly candidates: readonly string[];
  readonly status: 'open' | 'done' | 'dismissed';
  /** Krok SR2: kontakt zájemce rozpoznaný v těle notifikace portálu, nebo `null`. */
  readonly parsedInquiry: { readonly name: string | null; readonly email: string | null; readonly phone: string | null; readonly message: string | null } | null;
}

export interface ConnectMailboxInput {
  readonly preset: MailboxPresetKey;
  readonly host?: string;
  readonly port?: number;
  readonly username: string;
  readonly password: string;
  readonly shareWithOwner?: boolean;
  /** Krok MAIL1: odchozí server (jen u „vlastní"). */
  readonly smtpHost?: string;
  readonly smtpPort?: number;
  /** `false` = schránka má jen číst (žádné odesílání z ní). */
  readonly sending?: boolean;
}

/** Sdílený klíč react-query: Dnešek („Na tobě") i tabule čtou TÁTÁŽ data. */
export const MAILBOX_CARDS_QUERY_KEY = ['mailbox', 'cards'] as const;

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as { title?: unknown; type?: unknown } | null;
    throw new DomainApiError(
      typeof problem?.title === 'string' ? problem.title : `HTTP ${res.status}`,
      res.status,
      typeof problem?.type === 'string' ? problem.type : undefined,
    );
  }
  return (await res.json()) as T;
}

function send(method: string, path: string, body?: unknown): Promise<Response> {
  return fetch(`${baseUrl}${path}`, {
    method,
    credentials: 'include',
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
}

export async function getMailbox(): Promise<MailboxViewDto> {
  return json<MailboxViewDto>(await send('GET', '/v1/me/mailbox'));
}

export async function getMailboxCards(): Promise<readonly MailCardDto[]> {
  const body = await json<{ items?: readonly MailCardDto[] }>(await send('GET', '/v1/me/mailbox/cards'));
  return body.items ?? [];
}

/**
 * „Ověřit připojení" — nic neuloží. `code` je strojový kód chyby (UI ho
 * přeloží). Krok MAIL1: dva výsledky, čtení a odesílání zvlášť.
 */
export async function testMailbox(input: ConnectMailboxInput): Promise<MailboxTestDto> {
  return json<MailboxTestDto>(await send('POST', '/v1/me/mailbox/test', input));
}

export async function connectMailbox(input: ConnectMailboxInput): Promise<MailboxViewDto> {
  return json<MailboxViewDto>(await send('PUT', '/v1/me/mailbox', input));
}

/** Odpojit = server smaže heslo i všechny stažené e-maily. */
export async function disconnectMailbox(): Promise<{ readonly disconnected: boolean; readonly deletedMails: number }> {
  return json(await send('DELETE', '/v1/me/mailbox'));
}

/** Vyřízeno / zahodit — mění JEN stav karty (fakturu ani zakázku ne). */
export async function resolveMailCard(id: string, action: 'done' | 'dismissed'): Promise<MailCardDto> {
  return json<MailCardDto>(await send('POST', `/v1/me/mailbox/cards/${encodeURIComponent(id)}/resolve`, { action }));
}

export interface MailReplyResultDto {
  readonly sent: boolean;
  readonly route: 'maklerova-schranka' | 'system' | null;
  /** `null` = odesláno. Jinak strojový důvod (hlášku vybere katalog). */
  readonly blockReason: string | null;
}

/**
 * Krok MAIL1 bod 2 — „Odpovědět" na kartu e-mailu. Prostý text, odeslání
 * ze schránky makléře, vlákno přes `In-Reply-To`. Když je odesílání vypnuté,
 * vrátí `sent: false` + důvod — NIKDY tiché „odesláno".
 */
export async function replyMailCard(id: string, text: string): Promise<MailReplyResultDto> {
  return json<MailReplyResultDto>(
    await send('POST', `/v1/me/mailbox/cards/${encodeURIComponent(id)}/reply`, { text }),
  );
}

export interface SavedAttachmentDto {
  readonly documentId: string;
  /** `true` = tutéž přílohu zakázka už má (druhý klik nezaloží druhý dokument). */
  readonly duplicate: boolean;
  readonly filename: string;
}

/**
 * Krok PROP5 bod 5 — „Uložit přílohu k zakázce". Server si přílohu dotáhne ze
 * schránky makléře (jen ke čtení), proženě ji antivirem a uloží jako dokument
 * zakázky s odkazem na původní e-mail. Příloha se vybírá POZICÍ v seznamu —
 * názvy souborů se v jedné zprávě opakují.
 */
export async function saveMailAttachment(id: string, index: number): Promise<SavedAttachmentDto> {
  return json<SavedAttachmentDto>(
    await send('POST', `/v1/me/mailbox/cards/${encodeURIComponent(id)}/attachments/${String(index)}/save`),
  );
}
